import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/partner-lead-response")({
  validateSearch: (search: Record<string, unknown>) => ({ token: typeof search["token"] === "string" ? search["token"] : "" }),
  head: () => ({ meta: [
    { title: "Respond to a Shields cleaning enquiry" },
    { name: "robots", content: "noindex, nofollow, noarchive" },
    { name: "referrer", content: "no-referrer" },
  ] }),
  component: PartnerLeadResponsePage,
});

function PartnerLeadResponsePage() {
  const { token } = Route.useSearch();
  const [price, setPrice] = useState("");
  const [scopeNote, setScopeNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true); setError(""); setMessage("");
    const amount = Number(price);
    if (!Number.isFinite(amount) || amount < 1 || amount > 100000) {
      setError("Enter a proposed price between £1 and £100,000.");
      setBusy(false); return;
    }
    if (token.length < 30) {
      setError("This response link is missing or incomplete. Open the full link from your Shields email.");
      setBusy(false); return;
    }
    try {
      const response = await fetch("/api/public/exterior-lead-response", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, pricePence: Math.round(amount * 100), scopeNote: scopeNote.trim() }),
      });
      const result = await response.json() as { ok?: boolean; message?: string; error?: string };
      if (!response.ok || !result.ok) throw new Error(result.error || "We couldn't save your response.");
      setMessage(result.message || "Your proposed price and scope have been sent to Shields.");
      setSubmitted(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We couldn't save your response. Please contact Shields.");
    } finally { setBusy(false); }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#fbfcff_0%,#f1f5fa_100%)] px-4 py-10 text-[#071a38]">
      <section className="w-full max-w-xl rounded-3xl border border-[#d9e2ec] bg-white p-6 shadow-[0_22px_70px_-42px_rgba(7,26,56,.35)] sm:p-9">
        <p className="text-xs font-black uppercase tracking-[.2em] text-[#a47b32]">BookwithShields · Partner response</p>
        <h1 className="mt-3 text-2xl font-black">Send your proposed price</h1>
        <p className="mt-2 text-sm leading-6 text-[#506782]">Enter the price you would charge and exactly what is included. Shields will review your proposal before a final quote is sent to the customer. This is not a confirmed booking.</p>
        {submitted ? (
          <div role="status" className="mt-6 rounded-2xl border border-[#b7ddc8] bg-[#f0faf4] p-5 text-sm leading-6 text-[#245b3e]">
            <strong className="block">Response received</strong>{message}
          </div>
        ) : (
          <form className="mt-6 space-y-5" onSubmit={(event) => void submit(event)}>
            <label className="block text-sm font-bold">Your proposed total price (£)
              <Input className="mt-2 min-h-12" type="number" min="1" max="100000" step="0.01" required value={price} onChange={(event) => setPrice(event.target.value)} placeholder="e.g. 145.00" />
            </label>
            <label className="block text-sm font-bold">Scope and assumptions
              <Textarea className="mt-2 min-h-36" required minLength={10} maxLength={2000} value={scopeNote} onChange={(event) => setScopeNote(event.target.value)} placeholder="Describe the work included, any access requirements, exclusions, preparation, and anything that could change the price." />
            </label>
            <p className="text-xs leading-5 text-[#65778d]">Your secure link is single-use and expires after 72 hours. Do not forward it. If you need a new link, contact Shields.</p>
            {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
            <Button className="min-h-12 w-full bg-[#071a38] hover:bg-[#102c52]" type="submit" disabled={busy || token.length < 30}>{busy ? "Sending response…" : "Send price and scope"}</Button>
          </form>
        )}
        {message && !submitted && <p role="status" className="mt-4 text-sm">{message}</p>}
      </section>
    </main>
  );
}
